import { apiClient } from './api.client';
import type {
  Bookmark,
  BookmarkFolder,
  CreateBookmarkRequest,
  UpdateBookmarkRequest,
  CreateFolderRequest,
  UpdateFolderRequest
} from '@/types/api.types';

/**
 * Bookmark Service
 * Manages message bookmarks and folders
 */
export const bookmarksService = {
  /**
   * Create or update a bookmark
   */
  async createBookmark(input: CreateBookmarkRequest): Promise<Bookmark> {
    const response = await apiClient.post<Bookmark>('/bookmarks', input);
    return response.data;
  },

  /**
   * Remove a bookmark
   */
  async removeBookmark(messageId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/bookmarks/${messageId}`);
    return response.data;
  },

  /**
   * Update bookmark (move to folder, change note)
   */
  async updateBookmark(messageId: string, input: UpdateBookmarkRequest): Promise<Bookmark> {
    const response = await apiClient.patch<Bookmark>(`/bookmarks/${messageId}`, input);
    return response.data;
  },

  /**
   * Get user's bookmarks with optional folder filter
   */
  async getUserBookmarks(folderId?: string | null): Promise<Bookmark[]> {
    const params = folderId !== undefined ? { folderId } : undefined;
    const response = await apiClient.get<Bookmark[]>('/bookmarks', { params });
    return response.data;
  },

  /**
   * Check if a message is bookmarked
   */
  async isBookmarked(messageId: string): Promise<{ bookmarked: boolean }> {
    const response = await apiClient.get<{ bookmarked: boolean }>(`/bookmarks/${messageId}/status`);
    return response.data;
  },

  // === FOLDER MANAGEMENT ===

  /**
   * Create a bookmark folder
   */
  async createFolder(input: CreateFolderRequest): Promise<BookmarkFolder> {
    const response = await apiClient.post<BookmarkFolder>('/bookmarks/folders', input);
    return response.data;
  },

  /**
   * Update folder
   */
  async updateFolder(folderId: string, input: UpdateFolderRequest): Promise<BookmarkFolder> {
    const response = await apiClient.patch<BookmarkFolder>(`/bookmarks/folders/${folderId}`, input);
    return response.data;
  },

  /**
   * Delete folder (bookmarks move to no folder)
   */
  async deleteFolder(folderId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/bookmarks/folders/${folderId}`);
    return response.data;
  },

  /**
   * Get user's folders with bookmark counts
   */
  async getUserFolders(): Promise<BookmarkFolder[]> {
    const response = await apiClient.get<BookmarkFolder[]>('/bookmarks/folders');
    return response.data;
  },
};
